import { LADDER, STEPS_PER_ROUND, type SpentStep, type Track, type Verdict } from "../../shared/domain.ts";

/**
 * One player's attempt to identify a single Track.
 *
 * Deliberately free of audio and UI so Verdict logic and Step accounting can be
 * verified without a browser. The Ladder is the only clock that matters here.
 */

export type RoundStatus = "playing" | "won" | "revealed";

export interface RoundState {
  answer: Track;
  spent: SpentStep[];
  status: RoundStatus;
}

export function createRound(answer: Track): RoundState {
  return { answer, spent: [], status: "playing" };
}

/** How much audio the player has earned right now. */
export function currentDuration(state: RoundState): number {
  const index = Math.min(state.spent.length, LADDER.length - 1);
  return LADDER[index]!;
}

/** Which Step the player is on, 1-based, for display. */
export function currentStep(state: RoundState): number {
  return Math.min(state.spent.length + 1, STEPS_PER_ROUND);
}

/** Steps spent — this is the Round's contribution to the Daily Set's Score. */
export function stepsSpent(state: RoundState): number {
  return state.spent.length;
}

export function verdictFor(answer: Track, guess: Track): Verdict {
  if (guess.id === answer.id) return "exact";
  // Artist Match is on the primary artist, compared by id so punctuation and
  // "feat." spellings can't produce a false grey.
  if (guess.artistId === answer.artistId) return "artist-match";
  return "miss";
}

function advance(state: RoundState, step: SpentStep): RoundState {
  const spent = [...state.spent, step];

  if (step.verdict === "exact") return { ...state, spent, status: "won" };

  return {
    ...state,
    spent,
    status: spent.length >= STEPS_PER_ROUND ? "revealed" : "playing",
  };
}

export function submitGuess(state: RoundState, guess: Track): RoundState {
  if (state.status !== "playing") return state;

  const verdict = verdictFor(state.answer, guess);
  return advance(state, { guessedTrackId: guess.id, verdict });
}

/** A Skip costs exactly what a wrong Guess costs — no free information. */
export function skip(state: RoundState): RoundState {
  if (state.status !== "playing") return state;
  return advance(state, { guessedTrackId: null, verdict: "skipped" });
}
